// OWNED BY the calls implementation agent (see ../CallsTab.tsx ownership note).
//
// Slim audio player for a call recording — a fresh, compact take on the legacy
// CallsTableAudioPlayer with the same load-bearing behaviors ported:
// - Recording URL: replica-vs-provider pick + onError fallback through the
//   remaining links (getCallAudioUrlCandidates in ./callsData).
// - Download: ALWAYS through GET /api/v1/calls/recording/download with the
//   Firebase ID token — direct provider links are CORS-less so a client-side
//   fetch of them fails silently for some calls; the backend also authorizes
//   via getAccessibleVirtualNumbersForUser and resolves the URL server-side.
// - Playback speed cycle 1x → 2x, single-player coordination via the
//   isActivePlayer/onBecameActive contract (parent tracks the playing row).

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  Button,
  Slider,
  IconButton,
  Typography,
  CircularProgress
} from "@material-ui/core";
import { Download, Pause, PlayArrow } from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import { firebaseApp } from "firebaseConfig";
import { WEBOT_SERVER_ENDPOINT } from "logic/Consts";
import { formatCallDuration, getCallAudioUrlCandidates } from "./callsData";
import type { CallSummary } from "../../../../../../@types/sharedSchema";

const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 1.75, 2] as const;

type CallAudioPlayerProps = {
  call: CallSummary;
  /** False when another row's player took over — this player must pause. */
  isActivePlayer: boolean;
  /** Called right before playback starts so the parent can pause the others. */
  onBecameActive: () => void;
};

export function CallAudioPlayer({
  call,
  isActivePlayer,
  onBecameActive
}: CallAudioPlayerProps): JSX.Element {
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(0);
  const [srcIndex, setSrcIndex] = useState(0);
  const [audioFailed, setAudioFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const srcCandidates = useMemo(() => getCallAudioUrlCandidates(call), [call]);
  const src = srcCandidates[srcIndex];

  // Single-player coordination: pause when another player becomes active.
  useEffect(() => {
    if (isActivePlayer || !isPlaying) return;
    audioRef.current?.pause();
    setIsPlaying(false);
  }, [isActivePlayer, isPlaying]);

  const handleAudioError = (): void => {
    setIsPlaying(false);
    setReady(false);
    // Fall back to the next recording link; give up after the last one.
    if (srcIndex + 1 < srcCandidates.length) setSrcIndex(srcIndex + 1);
    else setAudioFailed(true);
  };

  const handleTogglePlay = (): void => {
    const audio = audioRef.current;
    if (!audio || audioFailed) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    onBecameActive();
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch(() => handleAudioError());
  };

  const handleLoadedMetadata = (): void => {
    const audio = audioRef.current;
    if (!audio) return;
    setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    setReady(true);
    // playbackRate resets when the src changes — re-apply the chosen speed.
    audio.playbackRate = PLAYBACK_SPEEDS[speedIndex];
  };

  const handleSeek = (_event: unknown, value: number | number[]): void => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextPosition = Array.isArray(value) ? value[0] : value;
    audio.currentTime = nextPosition;
    setPosition(nextPosition);
  };

  const handleSpeedChange = (): void => {
    const nextIndex = (speedIndex + 1) % PLAYBACK_SPEEDS.length;
    setSpeedIndex(nextIndex);
    if (audioRef.current) audioRef.current.playbackRate = PLAYBACK_SPEEDS[nextIndex];
  };

  const handleDownload = async (): Promise<void> => {
    if (downloading) return;

    const { id: callId, virtualNumber } = call;
    if (!callId || !virtualNumber) {
      enqueueSnackbar("הורדת ההקלטה נכשלה — פרטי השיחה חסרים", {
        variant: "error",
        autoHideDuration: 4000
      });
      return;
    }

    try {
      setDownloading(true);

      const token = await firebaseApp.auth().currentUser?.getIdToken();
      if (!token) throw new Error("Not authenticated");

      const params = new URLSearchParams({ virtualNumber, callId });
      const response = await fetch(
        `${WEBOT_SERVER_ENDPOINT}/api/v1/calls/recording/download?${params.toString()}`,
        { headers: { Authorization: token } }
      );

      if (!response.ok) throw new Error(`HTTP error status: ${response.status}`);

      const blob = await response.blob();
      const fileName =
        parseContentDispositionFileName(response.headers.get("content-disposition")) ??
        `webot-call-${callId}.mp3`;

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Call recording download failed:", error);
      enqueueSnackbar("הורדת ההקלטה נכשלה", { variant: "error", autoHideDuration: 4000 });
    } finally {
      setDownloading(false);
    }
  };

  const controlsDisabled = !ready || audioFailed;

  return (
    <Box
      sx={{ width: "100%", borderRadius: 1.5, bgcolor: "background.neutral", px: 1.5, py: 0.75 }}
    >
      {/* The strip is RTL like the rest of the page ON PURPOSE: MUI's Slider
          takes its direction from theme.direction (rtl), NOT from a DOM dir
          attribute — inside a forced dir="ltr" island its thumb/track/pointer
          math runs visually backwards. */}
      <Stack direction="row" alignItems="center" gap={0.5}>
        <IconButton
          size="small"
          color="primary"
          disabled={controlsDisabled}
          onClick={handleTogglePlay}
          aria-label={isPlaying ? "השהיה" : "ניגון"}
        >
          {!ready && !audioFailed ? (
            <CircularProgress size={18} />
          ) : isPlaying ? (
            <Pause fontSize="small" />
          ) : (
            <PlayArrow fontSize="small" />
          )}
        </IconButton>
        <Slider
          size="small"
          aria-label="מיקום בהקלטה"
          value={Math.min(position, duration || 0)}
          max={duration || 0}
          disabled={controlsDisabled}
          onChange={handleSeek}
          sx={{ mx: 1.5, flex: 1, minWidth: 60 }}
        />
        <Typography
          dir="ltr" // keep "position / total" order — bidi flips it in RTL flow
          variant="caption"
          sx={{ color: "text.secondary", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
        >
          {formatCallDuration(Math.floor(position))} / {formatCallDuration(Math.floor(duration))}
        </Typography>
        <Button
          size="small"
          color="inherit"
          disabled={controlsDisabled}
          onClick={handleSpeedChange}
          sx={{ minWidth: 44, color: "text.secondary" }}
        >
          {`${PLAYBACK_SPEEDS[speedIndex]}x`}
        </Button>
        <IconButton
          size="small"
          disabled={downloading}
          onClick={handleDownload}
          aria-label="הורדת ההקלטה"
        >
          {downloading ? <CircularProgress size={18} /> : <Download fontSize="small" />}
        </IconButton>
      </Stack>
      {audioFailed && (
        <Typography variant="caption" sx={{ display: "block", color: "error.main", mt: 0.5 }}>
          נגינת ההקלטה נכשלה — אפשר לנסות להוריד אותה
        </Typography>
      )}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={() => setPosition(audioRef.current?.currentTime ?? 0)}
        onEnded={() => {
          setIsPlaying(false);
          setPosition(0);
        }}
        onError={handleAudioError}
      />
    </Box>
  );
}

function parseContentDispositionFileName(header: string | null): string | undefined {
  if (!header) return undefined;
  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : undefined;
}
